import { z } from "zod";

// ── JSONL message shape (Claude Code session log) ───────────

export const SessionMessage = z.object({
  type: z.string(),
  role: z.string().optional(),
  message: z.unknown().optional(),
  timestamp: z.string().optional(),
  usage: z
    .object({
      input_tokens: z.number().optional(),
      output_tokens: z.number().optional(),
      cache_read_input_tokens: z.number().optional(),
      cache_creation_input_tokens: z.number().optional(),
    })
    .optional(),
  model: z.string().optional(),
  tool_name: z.string().optional(),
  tool_input: z.unknown().optional(),
});
export type SessionMessage = z.infer<typeof SessionMessage>;

// ── Token source hierarchy ──────────────────────────────────
// api > stats-cache > tokenizer > none (in order of accuracy)

export const TokenSource = z.enum(["api", "stats-cache", "tokenizer", "none"]);
export type TokenSource = z.infer<typeof TokenSource>;

// ── Normalized session record ───────────────────────────────

export const NormalizedSession = z.object({
  sessionId: z.string(),
  provider: z.string(),
  model: z.string(),
  inputTokens: z.number(),
  outputTokens: z.number(),
  cacheReadTokens: z.number(),
  cacheCreateTokens: z.number(),
  toolCalls: z.number(),
  filesTouched: z.array(z.string()),
  sessionStart: z.number(),
  sessionEnd: z.number().optional(),
  activeMinutes: z.number().optional(),
  conversationText: z.string(),
  tokenSource: TokenSource.default("none"),
});
export type NormalizedSession = z.infer<typeof NormalizedSession>;

// ── Auto-tag result ─────────────────────────────────────────

export const TagResult = z.object({
  featureId: z.string().optional(),
  confidence: z.number(),
  source: z.enum(["explicit", "branch", "keyword", "filepath", "none"]),
  status: z.enum(["auto-tagged", "likely", "untagged"]),
});
export type TagResult = z.infer<typeof TagResult>;

// ── Feature registry (read from .fathom/registry.json) ───

export const RegistryFeature = z.object({
  id: z.string(),
  name: z.string(),
  keywords: z.array(z.string()),
  file_patterns: z.array(z.string()),
  git_branch_patterns: z.array(z.string()),
  estimated_tokens: z.number(),
  status: z.string(),
});
export type RegistryFeature = z.infer<typeof RegistryFeature>;

export const Registry = z.object({
  project: z.string(),
  features: z.array(RegistryFeature),
  generatedAt: z.string(),
});
export type Registry = z.infer<typeof Registry>;
